import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";
import { canAccessScheduleCalendar } from "./appChromeAccess";
import { getApiBase } from "../apiBase";
import { isCommercialProjectRow, readCommercialDomainsForRow } from "./commercialProjectAcf";
import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { isCvinExpansion, formatExpansionWorkOrderForDisplay } from "../projectGridColumns";
import { getViewerEmailFromStorage, canViewerAccessPanel } from "./userMatrixStorage";
import { canEditCalendarEventType } from "./commercialRoleAccess";
import {
  formatConstructionScheduleDetail,
  readConstructionSchedule,
  readFiberSchedule,
  readNetworkingCalendarEvents,
} from "./commercialScheduleApi";
import { buildDailyProgressReportCalendarEventsForRow } from "./expansionProgressUtils";
import {
  applyConstructionLeadColorsToEvents,
  buildConstructionLeadColorMap,
  formatConstructionLeadLabel,
  isConstructionOnlyFilter,
  listTrackedConstructionLeadEmails,
  resolveConstructionLeadKey,
} from "./constructionCalendarUtils";
import {
  applyCrewColorsToEvents,
  buildCrewReporterColorMap,
  formatCrewReporterLabel,
  isCrewReportsOnlyFilter,
  listTrackedCrewReporterEmails,
} from "./crewReportCalendarUtils";
import InspectorReportCalendarModal from "./InspectorReportCalendarModal";
import {
  applyInspectorColorsToEvents,
  buildInspectorColorMap,
  formatInspectorLabel,
  isInspectorReportsOnlyFilter,
  isWeekday,
  listTrackedInspectorEmails,
  parseMonthInputValue,
} from "./inspectorReportCalendarUtils";
import { isPluginEnabled, PLUGIN_IDS } from "./registry";
import ScheduleCalendarDayModal from "./ScheduleCalendarDayModal";
import ScheduleCalendarEventModal from "./ScheduleCalendarEventModal";
import "./workflowSchedule.css";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const FILTER_STORAGE_KEY = "pb.scheduleCalendar.filters.v2";
const INSPECTOR_FOCUS_MONTH_KEY = "pb.scheduleCalendar.inspectorFocusMonth.v1";
const CREW_FOCUS_MONTH_KEY = "pb.scheduleCalendar.crewFocusMonth.v1";
const CONSTRUCTION_FOCUS_MONTH_KEY = "pb.scheduleCalendar.constructionFocusMonth.v1";

const BASE_EVENT_TYPES = {
  construction: {
    id: "construction",
    label: "Construction",
    short: "C",
    color: "#2563eb",
  },
  splicing: {
    id: "splicing",
    label: "Splicing",
    short: "S",
    color: "#7c3aed",
  },
  networking: {
    id: "networking",
    label: "Networking",
    short: "N",
    color: "#059669",
  },
};

const INSPECTOR_REPORTS_EVENT_TYPE = {
  id: "inspectorReports",
  label: "Inspector Reports",
  short: "IR",
  color: "#d97706",
};

const CREW_REPORTS_EVENT_TYPE = {
  id: "crewReports",
  label: "Crew Reports",
  short: "CR",
  color: "#0d9488",
};

function eventTypesForViewer(canAccessExpansion) {
  if (!canAccessExpansion) return BASE_EVENT_TYPES;
  return {
    ...BASE_EVENT_TYPES,
    inspectorReports: INSPECTOR_REPORTS_EVENT_TYPE,
    crewReports: CREW_REPORTS_EVENT_TYPE,
  };
}

const DEFAULT_FILTERS = {
  construction: true,
  splicing: true,
  networking: true,
  inspectorReports: true,
  crewReports: true,
};

function loadStoredFilters() {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) {
      // Migrate v1 filters if present.
      const legacy = localStorage.getItem("pb.scheduleCalendar.filters.v1");
      if (legacy) {
        const parsed = JSON.parse(legacy);
        return {
          construction: parsed.construction !== false,
          splicing: parsed.splicing !== false,
          networking: parsed.networking !== false,
          inspectorReports: parsed.inspectorReports !== false,
          crewReports: parsed.crewReports !== false,
        };
      }
      return { ...DEFAULT_FILTERS };
    }
    const parsed = JSON.parse(raw);
    return {
      construction: parsed.construction !== false,
      splicing: parsed.splicing !== false,
      networking: parsed.networking !== false,
      inspectorReports: parsed.inspectorReports !== false,
      crewReports: parsed.crewReports !== false,
    };
  } catch {
    return { ...DEFAULT_FILTERS };
  }
}

function loadStoredFocusMonth(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = String(raw).trim();
    return parseMonthInputValue(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function loadStoredInspectorFocusMonth() {
  return loadStoredFocusMonth(INSPECTOR_FOCUS_MONTH_KEY);
}

function loadStoredCrewFocusMonth() {
  return loadStoredFocusMonth(CREW_FOCUS_MONTH_KEY);
}

function loadStoredConstructionFocusMonth() {
  return loadStoredFocusMonth(CONSTRUCTION_FOCUS_MONTH_KEY);
}

function parseDateOnly(value) {
  if (value == null || String(value).trim() === "") return null;
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function addMonths(date, offset) {
  return new Date(date.getFullYear(), date.getMonth() + offset, 1);
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dateInRange(day, start, end) {
  const t = day.getTime();
  return t >= start.getTime() && t <= end.getTime();
}

function eventOnCalendarDay(event, day) {
  if (!dateInRange(day, event.start, event.end)) return false;
  if (event.eventType === BASE_EVENT_TYPES.construction.id && event.includeWeekends === false) {
    const weekday = day.getDay();
    if (weekday === 0 || weekday === 6) return false;
  }
  return true;
}

function formatMonthTitle(date) {
  return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function formatRangeTitle(months) {
  if (months.length === 0) return "";
  const first = months[0];
  const last = months[months.length - 1];
  return `${first.toLocaleDateString(undefined, { month: "short", year: "numeric" })} – ${last.toLocaleDateString(undefined, { month: "short", year: "numeric" })}`;
}

function buildMonthGrid(monthDate) {
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);
  const gridStart = new Date(monthStart);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  const days = [];
  const cursor = new Date(gridStart);
  while (cursor <= monthEnd || cursor.getDay() !== 0) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
    if (days.length > 42) break;
  }
  while (days.length % 7 !== 0) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function dayKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function scheduleSpansRange(startStr, endStr, rangeStart, rangeEnd) {
  const start = parseDateOnly(startStr);
  const end = parseDateOnly(endStr);
  if (!start || !end) return null;
  if (start > rangeEnd || end < rangeStart) return null;
  return { start, end };
}

function buildCalendarEventsForRow(row, rangeStart, rangeEnd) {
  const domains = readCommercialDomainsForRow(row, row.acf_domains);
  const summary = domains[COMMERCIAL_DOMAINS.SUMMARY] || {};
  const acfJson = row.acf_groups_json;
  const customer = summary.customer || row.customer || row.name || "—";
  const workOrder = isCvinExpansion(row)
    ? formatExpansionWorkOrderForDisplay(row) || summary.work_order || row.work_order || "—"
    : summary.work_order ?? row.work_order ?? "—";
  const projectId = row.id;
  const events = [];

  const construction = readConstructionSchedule(domains);
  const constructionSpan = scheduleSpansRange(
    construction.construction_start_date,
    construction.construction_end_date,
    rangeStart,
    rangeEnd
  );
  if (constructionSpan) {
    events.push({
      eventType: BASE_EVENT_TYPES.construction.id,
      eventKey: "construction",
      projectId,
      workOrder,
      customer,
      ...constructionSpan,
      color: BASE_EVENT_TYPES.construction.color,
      typeLabel: BASE_EVENT_TYPES.construction.label,
      detail: formatConstructionScheduleDetail(construction),
      includeWeekends: construction.construction_include_weekends === true,
      constructionLeadKey: resolveConstructionLeadKey(construction),
    });
  }

  const fiber = readFiberSchedule(domains);
  const splicingSpan = scheduleSpansRange(
    fiber.splicing_start_date,
    fiber.splicing_end_date,
    rangeStart,
    rangeEnd
  );
  if (splicingSpan) {
    events.push({
      eventType: BASE_EVENT_TYPES.splicing.id,
      eventKey: "splicing",
      projectId,
      workOrder,
      customer,
      ...splicingSpan,
      color: BASE_EVENT_TYPES.splicing.color,
      typeLabel: BASE_EVENT_TYPES.splicing.label,
      detail: fiber.splicer ? `Splicer: ${fiber.splicer}` : "",
    });
  }

  for (const install of readNetworkingCalendarEvents(domains, acfJson)) {
    const networkingSpan = scheduleSpansRange(
      install.startDate,
      install.endDate,
      rangeStart,
      rangeEnd
    );
    if (!networkingSpan) continue;
    events.push({
      eventType: BASE_EVENT_TYPES.networking.id,
      eventKey: `install-${install.installIndex}`,
      projectId,
      workOrder,
      customer,
      ...networkingSpan,
      color: BASE_EVENT_TYPES.networking.color,
      typeLabel: BASE_EVENT_TYPES.networking.label,
      detail: install.detail || "",
      isVoip: install.isVoip === true,
    });
  }

  return events;
}

function CalendarVoipIcon() {
  return (
    <svg
      className="construction-calendar-event-voip"
      viewBox="0 0 24 24"
      width="10"
      height="10"
      aria-hidden
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.07 21 3 13.93 3 5a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.2 2.2z"
      />
    </svg>
  );
}

function CalendarEventChip({ event, label, title, onEdit }) {
  const voipIcon = event.isVoip ? <CalendarVoipIcon /> : null;
  const inner = (
    <>
      <span className="construction-calendar-event-type">{event.typeShort}</span>
      {voipIcon}
      {label}
    </>
  );
  if (event.projectId == null || event.projectId === "") {
    return (
      <span
        className="construction-calendar-event construction-calendar-event--static"
        style={{ backgroundColor: event.color }}
        title={title}
      >
        {inner}
      </span>
    );
  }
  if (onEdit) {
    return (
      <button
        type="button"
        className="construction-calendar-event construction-calendar-event--button"
        style={{ backgroundColor: event.color }}
        title={title}
        onClick={() => onEdit(event)}
      >
        {inner}
      </button>
    );
  }
  return (
    <Link
      to={`/project/${event.projectId}`}
      className="construction-calendar-event"
      style={{ backgroundColor: event.color }}
      title={title}
    >
      {inner}
    </Link>
  );
}

function CalendarMonth({
  monthDate,
  today,
  eventsByDayKey,
  canEditEvent,
  onEventEdit,
  onDayDoubleClick,
  onReportView,
  inspectorReportsEventTypeId,
  crewReportsEventTypeId,
  inspectorReportsOnly,
  crewReportsOnly,
  constructionOnly,
  trackedInspectors,
  inspectorColorMap,
  inspectorsReportingByDay,
  trackedCrewReporters,
  crewReporterColorMap,
  crewReportingByDay,
}) {
  const days = useMemo(() => buildMonthGrid(monthDate), [monthDate]);
  const monthIndex = monthDate.getMonth();

  return (
    <section className="construction-calendar-month" aria-label={formatMonthTitle(monthDate)}>
      <h3 className="construction-calendar-month-title">{formatMonthTitle(monthDate)}</h3>
      <div className="construction-calendar-weekdays">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label} className="construction-calendar-weekday">
            {label}
          </span>
        ))}
      </div>
      <div className="construction-calendar-days">
        {days.map((day) => {
          const inMonth = day.getMonth() === monthIndex;
          const isToday = sameDay(day, today);
          const key = dayKey(day);
          const events = eventsByDayKey.get(key) || [];
          return (
            <div
              key={key}
              className={`construction-calendar-day${inMonth ? "" : " is-outside"}${isToday ? " is-today" : ""}${onDayDoubleClick && inMonth ? " is-schedulable" : ""}`}
              title={onDayDoubleClick && inMonth ? "Double-click to add a schedule" : undefined}
              onDoubleClick={
                onDayDoubleClick && inMonth
                  ? (e) => {
                      e.preventDefault();
                      onDayDoubleClick(day);
                    }
                  : undefined
              }
            >
              <span className="construction-calendar-day-number">{day.getDate()}</span>
              <div className="construction-calendar-day-events">
                {events.map((event) => {
                  const isInspectorReport = event.eventType === inspectorReportsEventTypeId;
                  const isCrewReport = event.eventType === crewReportsEventTypeId;
                  const isDailyReport = isInspectorReport || isCrewReport;
                  const isConstruction = event.eventType === BASE_EVENT_TYPES.construction.id;
                  const label = isDailyReport
                    ? event.segment || event.workOrder
                    : event.workOrder;
                  const title = [
                    event.typeLabel,
                    isInspectorReport
                      ? [
                          event.inspectorLabel || "Unknown inspector",
                          `Segment ${event.segment || "—"} · ${event.workOrder} · ${event.customer}`,
                        ].join(" · ")
                      : isCrewReport
                        ? [
                            event.constructionLeadLabel || "Unknown construction lead",
                            `Segment ${event.segment || "—"} · ${event.workOrder} · ${event.customer}`,
                          ].join(" · ")
                        : constructionOnly && isConstruction
                          ? [
                              event.constructionLeadLabel || "Unknown construction lead",
                              `${label} · ${event.customer}`,
                            ].join(" · ")
                          : `${label} · ${event.customer}`,
                    event.detail,
                  ]
                    .filter(Boolean)
                    .join(" — ");
                  const eventDomKey = `${event.eventType}-${event.projectId}-${event.eventKey || "0"}-${key}`;
                  return (
                    <CalendarEventChip
                      key={eventDomKey}
                      event={event}
                      label={label}
                      title={title}
                      onEdit={
                        isDailyReport
                          ? onReportView
                          : canEditEvent(event)
                            ? onEventEdit
                            : undefined
                      }
                    />
                  );
                })}
              </div>
              {inspectorReportsOnly && inMonth && isWeekday(day) && day.getTime() <= today.getTime() ? (
                <div className="inspector-calendar-day-status" aria-label="Inspector attendance">
                  {trackedInspectors.map((inspectorKey) => {
                    const reported = inspectorsReportingByDay.get(key)?.has(inspectorKey);
                    const color = inspectorColorMap.get(inspectorKey) || "#94a3b8";
                    const name = formatInspectorLabel(inspectorKey);
                    return (
                      <span
                        key={`${key}-${inspectorKey}`}
                        className={`inspector-calendar-status-dot${reported ? " is-reported" : " is-missing"}`}
                        style={{ "--inspector-color": color }}
                        title={
                          reported
                            ? `${name} reported on this day`
                            : `${name} — no report filed`
                        }
                        aria-label={reported ? `${name} reported` : `${name} missing report`}
                      />
                    );
                  })}
                </div>
              ) : null}
              {crewReportsOnly && inMonth && isWeekday(day) && day.getTime() <= today.getTime() ? (
                <div className="inspector-calendar-day-status" aria-label="Crew attendance">
                  {trackedCrewReporters.map((leadKey) => {
                    const reported = crewReportingByDay.get(key)?.has(leadKey);
                    const color = crewReporterColorMap.get(leadKey) || "#94a3b8";
                    const name = formatCrewReporterLabel(leadKey);
                    return (
                      <span
                        key={`${key}-crew-${leadKey}`}
                        className={`inspector-calendar-status-dot${reported ? " is-reported" : " is-missing"}`}
                        style={{ "--inspector-color": color }}
                        title={
                          reported
                            ? `${name} reported on this day`
                            : `${name} — no report filed`
                        }
                        aria-label={reported ? `${name} reported` : `${name} missing report`}
                      />
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ScheduleMonthPicker({ focusMonth, onFocusMonthChange }) {
  return (
    <div className="inspector-calendar-month-picker">
      <label className="inspector-calendar-month-picker-label">
        <span>View month</span>
        <input
          type="month"
          className="commercial-field-input inspector-calendar-month-input"
          value={focusMonth || ""}
          onChange={(e) => onFocusMonthChange(e.target.value || null)}
        />
      </label>
      {focusMonth ? (
        <button
          type="button"
          className="email-triggers-button inspector-calendar-month-reset"
          onClick={() => onFocusMonthChange(null)}
        >
          Show last 2 months
        </button>
      ) : null}
    </div>
  );
}

function ConstructionLeadLegend({ trackedLeads, leadColorMap }) {
  if (!trackedLeads.length) return null;
  return (
    <div className="inspector-calendar-legend" role="list" aria-label="Construction lead colors">
      {trackedLeads.map((leadKey) => {
        const color = leadColorMap.get(leadKey) || "#94a3b8";
        return (
          <span key={leadKey} className="inspector-calendar-legend-item" role="listitem">
            <span
              className="inspector-calendar-legend-swatch"
              style={{ backgroundColor: color }}
              aria-hidden
            />
            {formatConstructionLeadLabel(leadKey)}
          </span>
        );
      })}
    </div>
  );
}

function CrewLegend({ trackedCrewReporters, crewReporterColorMap }) {
  if (!trackedCrewReporters.length) return null;
  return (
    <div className="inspector-calendar-legend" role="list" aria-label="Construction lead colors">
      {trackedCrewReporters.map((leadKey) => {
        const color = crewReporterColorMap.get(leadKey) || "#94a3b8";
        return (
          <span key={leadKey} className="inspector-calendar-legend-item" role="listitem">
            <span
              className="inspector-calendar-legend-swatch"
              style={{ backgroundColor: color }}
              aria-hidden
            />
            {formatCrewReporterLabel(leadKey)}
          </span>
        );
      })}
      <span className="inspector-calendar-legend-hint">
        Filled dots on weekdays = reported · hollow = missing
      </span>
    </div>
  );
}

function InspectorLegend({ trackedInspectors, inspectorColorMap }) {
  if (!trackedInspectors.length) return null;
  return (
    <div className="inspector-calendar-legend" role="list" aria-label="Inspector colors">
      {trackedInspectors.map((inspectorKey) => {
        const color = inspectorColorMap.get(inspectorKey) || "#94a3b8";
        return (
          <span key={inspectorKey} className="inspector-calendar-legend-item" role="listitem">
            <span
              className="inspector-calendar-legend-swatch"
              style={{ backgroundColor: color }}
              aria-hidden
            />
            {formatInspectorLabel(inspectorKey)}
          </span>
        );
      })}
      <span className="inspector-calendar-legend-hint">
        Filled dots on weekdays = reported · hollow = missing
      </span>
    </div>
  );
}

function ScheduleTypeFilters({ filters, onToggle, counts, eventTypes }) {
  return (
    <div className="schedule-calendar-filters" role="group" aria-label="Schedule event types">
      {Object.values(eventTypes).map((type) => {
        const active = filters[type.id];
        return (
          <button
            key={type.id}
            type="button"
            className={`schedule-calendar-filter${active ? " is-active" : ""}`}
            aria-pressed={active}
            onClick={() => onToggle(type.id)}
            style={
              active
                ? {
                    borderColor: type.color,
                    background: `${type.color}14`,
                    color: type.color,
                  }
                : undefined
            }
          >
            <span className="schedule-calendar-filter-swatch" style={{ backgroundColor: type.color }} aria-hidden />
            {type.label}
            <span className="schedule-calendar-filter-count">({counts[type.id] ?? 0})</span>
          </button>
        );
      })}
    </div>
  );
}

export default function ConstructionBoardPage({ apiUrl, getAuthHeaders }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(loadStoredFilters);
  const [editEvent, setEditEvent] = useState(null);
  const [createDay, setCreateDay] = useState(null);
  const [viewReportEvent, setViewReportEvent] = useState(null);
  const [inspectorFocusMonth, setInspectorFocusMonth] = useState(loadStoredInspectorFocusMonth);
  const [crewFocusMonth, setCrewFocusMonth] = useState(loadStoredCrewFocusMonth);
  const [constructionFocusMonth, setConstructionFocusMonth] = useState(loadStoredConstructionFocusMonth);
  const viewerEmail = getViewerEmailFromStorage();
  const userMatrixEnabled = isPluginEnabled(PLUGIN_IDS.USER_MATRIX);
  const canAccessExpansion = canViewerAccessPanel(viewerEmail, "expansion", userMatrixEnabled);
  const eventTypes = useMemo(() => eventTypesForViewer(canAccessExpansion), [canAccessExpansion]);
  const inspectorReportsOnly = useMemo(
    () => canAccessExpansion && isInspectorReportsOnlyFilter(filters),
    [canAccessExpansion, filters]
  );
  const crewReportsOnly = useMemo(
    () => canAccessExpansion && isCrewReportsOnlyFilter(filters),
    [canAccessExpansion, filters]
  );
  const constructionOnly = useMemo(
    () => isConstructionOnlyFilter(filters, { requireReportFiltersOff: canAccessExpansion }),
    [canAccessExpansion, filters]
  );
  const focusedMonthView = inspectorReportsOnly || crewReportsOnly || constructionOnly;
  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const visibleMonths = useMemo(() => {
    if (inspectorReportsOnly) {
      const focusDate = parseMonthInputValue(inspectorFocusMonth);
      if (focusDate) return [startOfMonth(focusDate)];
      return [addMonths(startOfMonth(today), -1), startOfMonth(today)];
    }
    if (crewReportsOnly) {
      const focusDate = parseMonthInputValue(crewFocusMonth);
      if (focusDate) return [startOfMonth(focusDate)];
      return [addMonths(startOfMonth(today), -1), startOfMonth(today)];
    }
    if (constructionOnly) {
      const focusDate = parseMonthInputValue(constructionFocusMonth);
      if (focusDate) return [startOfMonth(focusDate)];
      return [addMonths(startOfMonth(today), -1), startOfMonth(today)];
    }
    return [
      addMonths(startOfMonth(today), -1),
      startOfMonth(today),
      addMonths(startOfMonth(today), 1),
      addMonths(startOfMonth(today), 2),
    ];
  }, [
    inspectorReportsOnly,
    inspectorFocusMonth,
    crewReportsOnly,
    crewFocusMonth,
    constructionOnly,
    constructionFocusMonth,
    today,
  ]);

  const rangeStart = useMemo(() => startOfMonth(visibleMonths[0]), [visibleMonths]);
  const rangeEnd = useMemo(() => endOfMonth(visibleMonths[visibleMonths.length - 1]), [visibleMonths]);

  useEffect(() => {
    try {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    } catch {
      /* ignore quota */
    }
  }, [filters]);

  useEffect(() => {
    try {
      if (inspectorFocusMonth) {
        localStorage.setItem(INSPECTOR_FOCUS_MONTH_KEY, inspectorFocusMonth);
      } else {
        localStorage.removeItem(INSPECTOR_FOCUS_MONTH_KEY);
      }
    } catch {
      /* ignore quota */
    }
  }, [inspectorFocusMonth]);

  useEffect(() => {
    try {
      if (crewFocusMonth) {
        localStorage.setItem(CREW_FOCUS_MONTH_KEY, crewFocusMonth);
      } else {
        localStorage.removeItem(CREW_FOCUS_MONTH_KEY);
      }
    } catch {
      /* ignore quota */
    }
  }, [crewFocusMonth]);

  useEffect(() => {
    try {
      if (constructionFocusMonth) {
        localStorage.setItem(CONSTRUCTION_FOCUS_MONTH_KEY, constructionFocusMonth);
      } else {
        localStorage.removeItem(CONSTRUCTION_FOCUS_MONTH_KEY);
      }
    } catch {
      /* ignore quota */
    }
  }, [constructionFocusMonth]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const base = apiUrl || getApiBase();
      const { data } = await axios.get(`${base}/projects`, { headers: getAuthHeaders() });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not load projects.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, getAuthHeaders]);

  useEffect(() => {
    void load();
  }, [load]);

  const allEvents = useMemo(() => {
    const list = [];
    for (const row of rows) {
      if (isCommercialProjectRow(row)) {
        const rowEvents = buildCalendarEventsForRow(row, rangeStart, rangeEnd);
        for (const ev of rowEvents) {
          list.push({
            ...ev,
            typeShort: eventTypes[ev.eventType]?.short || "?",
          });
        }
      } else if (isCvinExpansion(row)) {
        // Expansion WOs support construction / splicing schedules (not networking).
        const rowEvents = buildCalendarEventsForRow(row, rangeStart, rangeEnd).filter(
          (ev) =>
            ev.eventType === BASE_EVENT_TYPES.construction.id ||
            ev.eventType === BASE_EVENT_TYPES.splicing.id
        );
        for (const ev of rowEvents) {
          list.push({
            ...ev,
            typeShort: eventTypes[ev.eventType]?.short || "?",
          });
        }
      }
      if (canAccessExpansion && isCvinExpansion(row)) {
        const reportEvents = buildDailyProgressReportCalendarEventsForRow(
          row,
          rangeStart,
          rangeEnd,
          {
            inspectorMeta: INSPECTOR_REPORTS_EVENT_TYPE,
            crewMeta: CREW_REPORTS_EVENT_TYPE,
          }
        );
        for (const ev of reportEvents) {
          list.push({
            ...ev,
            typeShort:
              ev.eventType === CREW_REPORTS_EVENT_TYPE.id
                ? eventTypes.crewReports?.short || "CR"
                : eventTypes.inspectorReports?.short || "IR",
          });
        }
      }
    }
    return list;
  }, [rows, rangeStart, rangeEnd, canAccessExpansion, eventTypes]);

  const typeCounts = useMemo(() => {
    const counts = {
      construction: 0,
      splicing: 0,
      networking: 0,
      inspectorReports: 0,
      crewReports: 0,
    };
    for (const ev of allEvents) {
      if (counts[ev.eventType] != null) counts[ev.eventType] += 1;
    }
    return counts;
  }, [allEvents]);

  const visibleEvents = useMemo(
    () => allEvents.filter((ev) => filters[ev.eventType] !== false),
    [allEvents, filters]
  );

  const inspectorReportEvents = useMemo(
    () => visibleEvents.filter((ev) => ev.eventType === INSPECTOR_REPORTS_EVENT_TYPE.id),
    [visibleEvents]
  );

  const crewReportEvents = useMemo(
    () => visibleEvents.filter((ev) => ev.eventType === CREW_REPORTS_EVENT_TYPE.id),
    [visibleEvents]
  );

  const trackedInspectors = useMemo(() => {
    if (!inspectorReportsOnly) return [];
    const extra = inspectorReportEvents.map((ev) => ev.inspectorKey).filter(Boolean);
    return listTrackedInspectorEmails(extra);
  }, [inspectorReportsOnly, inspectorReportEvents]);

  const inspectorColorMap = useMemo(
    () => buildInspectorColorMap(trackedInspectors),
    [trackedInspectors]
  );

  const trackedCrewReporters = useMemo(() => {
    if (!crewReportsOnly) return [];
    const extra = crewReportEvents.map((ev) => ev.constructionLeadKey).filter(Boolean);
    return listTrackedCrewReporterEmails(extra);
  }, [crewReportsOnly, crewReportEvents]);

  const crewReporterColorMap = useMemo(
    () => buildCrewReporterColorMap(trackedCrewReporters),
    [trackedCrewReporters]
  );

  const constructionEvents = useMemo(
    () => visibleEvents.filter((ev) => ev.eventType === BASE_EVENT_TYPES.construction.id),
    [visibleEvents]
  );

  const trackedConstructionLeads = useMemo(() => {
    if (!constructionOnly) return [];
    const extra = constructionEvents.map((ev) => ev.constructionLeadKey).filter(Boolean);
    return listTrackedConstructionLeadEmails(extra);
  }, [constructionOnly, constructionEvents]);

  const constructionLeadColorMap = useMemo(
    () => buildConstructionLeadColorMap(trackedConstructionLeads),
    [trackedConstructionLeads]
  );

  const displayEvents = useMemo(() => {
    if (inspectorReportsOnly) {
      return applyInspectorColorsToEvents(visibleEvents, inspectorColorMap);
    }
    if (crewReportsOnly) {
      return applyCrewColorsToEvents(visibleEvents, crewReporterColorMap);
    }
    if (constructionOnly) {
      return applyConstructionLeadColorsToEvents(visibleEvents, constructionLeadColorMap);
    }
    return visibleEvents;
  }, [
    inspectorReportsOnly,
    crewReportsOnly,
    constructionOnly,
    visibleEvents,
    inspectorColorMap,
    crewReporterColorMap,
    constructionLeadColorMap,
  ]);

  const inspectorsReportingByDay = useMemo(() => {
    const map = new Map();
    for (const ev of displayEvents) {
      if (ev.eventType !== INSPECTOR_REPORTS_EVENT_TYPE.id || !ev.inspectorKey) continue;
      const key = dayKey(ev.start);
      if (!map.has(key)) map.set(key, new Set());
      map.get(key).add(ev.inspectorKey);
    }
    return map;
  }, [displayEvents]);

  const crewReportingByDay = useMemo(() => {
    const map = new Map();
    for (const ev of displayEvents) {
      if (ev.eventType !== CREW_REPORTS_EVENT_TYPE.id || !ev.constructionLeadKey) continue;
      const key = dayKey(ev.start);
      if (!map.has(key)) map.set(key, new Set());
      map.get(key).add(ev.constructionLeadKey);
    }
    return map;
  }, [displayEvents]);

  const eventsByDayKey = useMemo(() => {
    const map = new Map();
    for (const monthDate of visibleMonths) {
      for (const day of buildMonthGrid(monthDate)) {
        const key = dayKey(day);
        const matches = displayEvents.filter((event) => eventOnCalendarDay(event, day));
        if (matches.length > 0) map.set(key, matches);
      }
    }
    return map;
  }, [visibleMonths, displayEvents]);

  const toggleFilter = (typeId) => {
    setFilters((prev) => ({ ...prev, [typeId]: !prev[typeId] }));
  };

  const editProjectRow = useMemo(() => {
    if (!editEvent?.projectId) return null;
    return rows.find((r) => r.id === editEvent.projectId) || null;
  }, [editEvent, rows]);

  const viewReportProjectRow = useMemo(() => {
    if (!viewReportEvent?.projectId) return null;
    return rows.find((r) => r.id === viewReportEvent.projectId) || null;
  }, [viewReportEvent, rows]);

  const canEditEvent = useCallback(
    (event) => canEditCalendarEventType(event.eventType, viewerEmail),
    [viewerEmail]
  );

  const editableScheduleTypes = useMemo(
    () =>
      Object.values(BASE_EVENT_TYPES).filter((type) =>
        canEditCalendarEventType(type.id, viewerEmail)
      ),
    [viewerEmail]
  );

  const canCreateScheduleFromDay =
    !inspectorReportsOnly && !crewReportsOnly && editableScheduleTypes.length > 0;

  const onEventEdit = useCallback((event) => {
    setEditEvent(event);
  }, []);

  const onDayDoubleClick = useCallback((day) => {
    setCreateDay(day);
  }, []);

  const onReportView = useCallback((event) => {
    setViewReportEvent(event);
  }, []);

  const calendarSubtitle = inspectorReportsOnly
    ? inspectorFocusMonth
      ? `Inspector reports for ${formatMonthTitle(parseMonthInputValue(inspectorFocusMonth) || today)}`
      : `Inspector reports for ${formatRangeTitle(visibleMonths)}`
    : crewReportsOnly
      ? crewFocusMonth
        ? `Crew reports for ${formatMonthTitle(parseMonthInputValue(crewFocusMonth) || today)}`
        : `Crew reports for ${formatRangeTitle(visibleMonths)}`
      : constructionOnly
        ? constructionFocusMonth
          ? `Construction schedules for ${formatMonthTitle(parseMonthInputValue(constructionFocusMonth) || today)}`
          : `Construction schedules for ${formatRangeTitle(visibleMonths)}`
        : canAccessExpansion
          ? "Construction, splicing, networking, inspector reports, and crew reports for"
          : "Construction, splicing, and networking schedules for";

  return (
    <div className="construction-board-page">
      <header className="construction-board-header">
        <div>
          <h2 className="grid-section-title">Schedule calendar</h2>
          <p className="project-detail-muted construction-board-subtitle">
            {focusedMonthView ? calendarSubtitle : `${calendarSubtitle} ${formatRangeTitle(visibleMonths)}.`}
            {canCreateScheduleFromDay ? " Double-click a day to add a schedule." : ""}
          </p>
          {inspectorReportsOnly ? (
            <ScheduleMonthPicker
              focusMonth={inspectorFocusMonth}
              onFocusMonthChange={setInspectorFocusMonth}
            />
          ) : null}
          {crewReportsOnly ? (
            <ScheduleMonthPicker
              focusMonth={crewFocusMonth}
              onFocusMonthChange={setCrewFocusMonth}
            />
          ) : null}
          {constructionOnly ? (
            <ScheduleMonthPicker
              focusMonth={constructionFocusMonth}
              onFocusMonthChange={setConstructionFocusMonth}
            />
          ) : null}
        </div>
        <ScheduleTypeFilters
          filters={filters}
          onToggle={toggleFilter}
          counts={typeCounts}
          eventTypes={eventTypes}
        />
      </header>

      {inspectorReportsOnly ? (
        <InspectorLegend trackedInspectors={trackedInspectors} inspectorColorMap={inspectorColorMap} />
      ) : null}
      {crewReportsOnly ? (
        <CrewLegend
          trackedCrewReporters={trackedCrewReporters}
          crewReporterColorMap={crewReporterColorMap}
        />
      ) : null}
      {constructionOnly ? (
        <ConstructionLeadLegend
          trackedLeads={trackedConstructionLeads}
          leadColorMap={constructionLeadColorMap}
        />
      ) : null}

      {loading ? <p className="project-detail-muted">Loading projects…</p> : null}
      {error ? (
        <p className="project-detail-error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && displayEvents.length === 0 ? (
        <p className="project-detail-muted">
          No scheduled events match the selected filters in this date range.
        </p>
      ) : (
        <p className="construction-calendar-count">
          {displayEvents.length} event{displayEvents.length === 1 ? "" : "s"} shown
        </p>
      )}

      <div
        className={`construction-calendar-grid${
          focusedMonthView ? " construction-calendar-grid--inspector-focus" : ""
        }${constructionOnly ? " construction-calendar-grid--lead-focus" : ""}`}
      >
        {visibleMonths.map((monthDate) => (
          <CalendarMonth
            key={`${monthDate.getFullYear()}-${monthDate.getMonth()}`}
            monthDate={monthDate}
            today={today}
            eventsByDayKey={eventsByDayKey}
            canEditEvent={canEditEvent}
            onEventEdit={onEventEdit}
            onDayDoubleClick={canCreateScheduleFromDay ? onDayDoubleClick : undefined}
            onReportView={onReportView}
            inspectorReportsEventTypeId={INSPECTOR_REPORTS_EVENT_TYPE.id}
            crewReportsEventTypeId={CREW_REPORTS_EVENT_TYPE.id}
            inspectorReportsOnly={inspectorReportsOnly}
            crewReportsOnly={crewReportsOnly}
            constructionOnly={constructionOnly}
            trackedInspectors={trackedInspectors}
            inspectorColorMap={inspectorColorMap}
            inspectorsReportingByDay={inspectorsReportingByDay}
            trackedCrewReporters={trackedCrewReporters}
            crewReporterColorMap={crewReporterColorMap}
            crewReportingByDay={crewReportingByDay}
          />
        ))}
      </div>

      {createDay ? (
        <ScheduleCalendarDayModal
          day={createDay}
          projects={rows}
          editableEventTypes={editableScheduleTypes}
          apiUrl={apiUrl}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setCreateDay(null)}
          onSaved={() => void load()}
        />
      ) : null}

      {editEvent && editProjectRow ? (
        <ScheduleCalendarEventModal
          event={editEvent}
          projectRow={editProjectRow}
          apiUrl={apiUrl}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setEditEvent(null)}
          onSaved={() => void load()}
        />
      ) : null}

      {viewReportEvent && viewReportProjectRow ? (
        <InspectorReportCalendarModal
          event={viewReportEvent}
          projectRow={viewReportProjectRow}
          onClose={() => setViewReportEvent(null)}
        />
      ) : null}
    </div>
  );
}

export function ConstructionBoardGate({ apiUrl, getAuthHeaders }) {
  if (!canAccessScheduleCalendar()) {
    return <Navigate to="/" replace />;
  }
  return <ConstructionBoardPage apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} />;
}
